import React, { useEffect, useState } from 'react';
import { Typography, Avatar, Space, Row, Col, Card, Spin, theme } from 'antd';
import { LockOutlined, GlobalOutlined, ShopOutlined, InboxOutlined, EditOutlined, StarFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHostProfile } from '../../../contextProviders/HostProfileProvider';
import { getIncomingBookings } from '../../../services/BookingService';
import SettingsCard from '../../../components/Settings/SettingsCard';
import SettingsPillButton from '../../../components/Settings/SettingsPillButton';
import SettingsPillBadge, { SettingsPillBadgeTone } from '../../../components/Settings/SettingsPillBadge';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title, Text, Paragraph } = Typography;

const VERIFICATION_TONE: Record<string, SettingsPillBadgeTone> = {
  NotStarted: 'default',
  Pending: 'processing',
  Verified: 'success',
  Rejected: 'error',
};

const VERIFICATION_LABEL_KEY: Record<string, string> = {
  NotStarted: TRANSLATIONS.HOST_VERIFICATION_NOT_STARTED,
  Pending: TRANSLATIONS.HOST_VERIFICATION_PENDING,
  Verified: TRANSLATIONS.HOST_VERIFICATION_VERIFIED,
  Rejected: TRANSLATIONS.HOST_VERIFICATION_REJECTED,
};

const formatMonthYear = (iso: string, locale: string) =>
  new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(new Date(iso));

/**
 * "Configuración > Perfil de anfitrión" -- read-only summary of the host
 * identity, folded in here as one more Settings section instead of its own
 * page (see the now-retired HostProfileSummaryPage). Only rendered when
 * hasHostProfile is true (see Settings.tsx's visibleSections filter);
 * editing still happens on /host/profile/edit, linked from each card here.
 */
const HostProfileSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t, i18n } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const navigate = useNavigate();
  const { hostProfile, loading } = useHostProfile();
  const [pendingIncoming, setPendingIncoming] = useState(0);

  useEffect(() => {
    getIncomingBookings()
      .then((bookings) => setPendingIncoming(bookings.filter((b) => b.status === 'Pending').length))
      .catch(() => undefined);
  }, []);

  if (loading || !hostProfile) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  const taxIdLabel = translate(t, hostProfile.accountType === 'Business' ? TRANSLATIONS.HOST_FIELD_TAX_ID_BUSINESS : TRANSLATIONS.HOST_FIELD_TAX_ID_INDIVIDUAL);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div>
          {/* Redundant on mobile -- the list->detail header (Settings.tsx) already shows this section's name. */}
          {!isMobile && (
            <Title level={4} style={{ margin: 0 }}>
              {translate(t, TRANSLATIONS.SETTINGS_NAV_HOST_PROFILE)}
            </Title>
          )}
          <Paragraph type="secondary" style={{ margin: '4px 0 0' }}>
            {translate(t, TRANSLATIONS.HOST_PROFILE_SUMMARY_SUBTITLE)}
          </Paragraph>
        </div>
        <SettingsPillBadge tone={VERIFICATION_TONE[hostProfile.idVerificationStatus]}>
          {translate(t, TRANSLATIONS.HOST_VERIFICATION_STATUS_LABEL)}: {translate(t, VERIFICATION_LABEL_KEY[hostProfile.idVerificationStatus])}
        </SettingsPillBadge>
      </div>

      <SettingsCard
        icon={<LockOutlined />}
        title={translate(t, TRANSLATIONS.HOST_EDIT_SECTION_LEGAL)}
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HOST_PROFILE_LEGAL_PRIVATE_BADGE)}
            </Text>
            <SettingsPillButton icon={<EditOutlined />} onClick={() => navigate('/host/profile/edit/legal')}>
              {translate(t, TRANSLATIONS.HOST_PROFILE_EDIT_BUTTON)}
            </SettingsPillButton>
          </Space>
        }
      >
        <Row gutter={[16, 8]}>
          <Col xs={24} sm={12}>
            <Text type="secondary">{translate(t, TRANSLATIONS.HOST_EDIT_ACCOUNT_TYPE_LABEL)}: </Text>
            <Text strong>{translate(t, hostProfile.accountType === 'Business' ? TRANSLATIONS.HOST_OPT_ACCOUNT_TYPE_BUSINESS : TRANSLATIONS.HOST_OPT_ACCOUNT_TYPE_INDIVIDUAL)}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">{taxIdLabel}: </Text>
            <Text strong>{hostProfile.taxId || '—'}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">{translate(t, TRANSLATIONS.HOST_FIELD_CONTACT_PHONE)}: </Text>
            <Text strong>{hostProfile.contactPhone}</Text>
          </Col>
          <Col xs={24} sm={12}>
            <Text type="secondary">{translate(t, TRANSLATIONS.HOST_FIELD_INSURANCE_POLICY_NUMBER)}: </Text>
            <Text strong type={hostProfile.insurancePolicyNumber ? undefined : 'secondary'}>
              {hostProfile.insurancePolicyNumber || translate(t, TRANSLATIONS.HOST_PROFILE_NOT_ADDED)}
            </Text>
          </Col>
        </Row>
      </SettingsCard>

      <SettingsCard
        icon={<GlobalOutlined />}
        title={translate(t, TRANSLATIONS.HOST_EDIT_SECTION_PUBLIC)}
        extra={
          <Space>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translate(t, TRANSLATIONS.HOST_PROFILE_PUBLIC_BADGE)}
            </Text>
            <SettingsPillButton icon={<EditOutlined />} onClick={() => navigate('/host/profile/edit/public')}>
              {translate(t, TRANSLATIONS.HOST_PROFILE_EDIT_BUTTON)}
            </SettingsPillButton>
          </Space>
        }
      >
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          <Avatar size={56} src={hostProfile.profilePhotoOrLogo || undefined} />
          <div>
            <Text strong style={{ fontSize: 15 }}>
              {hostProfile.displayName}
            </Text>
            <div>
              <Text type="secondary">
                {hostProfile.hostingSince && (
                  <>
                    {translate(t, TRANSLATIONS.HOST_PUBLIC_HOSTING_SINCE_LABEL)} {formatMonthYear(hostProfile.hostingSince, i18n.language)} ·{' '}
                  </>
                )}
                {hostProfile.aggregatedRating != null ? (
                  <>
                    <StarFilled style={{ color: token.colorWarning }} /> {hostProfile.aggregatedRating.toFixed(1)} ({hostProfile.reviewCount})
                  </>
                ) : (
                  translate(t, TRANSLATIONS.HOST_PROFILE_NO_REVIEWS_YET)
                )}
              </Text>
            </div>
          </div>
        </div>
        {hostProfile.tagline && <Paragraph style={{ marginTop: 16 }}>"{hostProfile.tagline}"</Paragraph>}
        {hostProfile.languagesSpoken && (
          <Space wrap style={{ marginTop: 8 }}>
            {hostProfile.languagesSpoken.split(',').filter(Boolean).map((lang) => (
              <SettingsPillBadge key={lang}>{lang}</SettingsPillBadge>
            ))}
          </Space>
        )}
      </SettingsCard>

      <Row gutter={16}>
        <Col xs={24} sm={12}>
          <Card
            hoverable
            onClick={() => navigate('/heavy-duty-machinery/mine')}
            style={{ borderRadius: 16 }}
            styles={{ body: { display: 'flex', alignItems: 'center', gap: 10, borderRadius: 16 } }}
          >
            <ShopOutlined style={{ fontSize: 20 }} />
            <span>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NAV_MY_SPACES)}{' '}
              <Text type="secondary">
                ({hostProfile.spacesCount} {translate(t, TRANSLATIONS.HOST_PROFILE_SPACES_ACTIVE_LABEL)})
              </Text>
            </span>
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card
            hoverable
            onClick={() => navigate('/heavy-duty-machinery/bookings/incoming')}
            style={{ borderRadius: 16 }}
            styles={{ body: { display: 'flex', alignItems: 'center', gap: 10, borderRadius: 16 } }}
          >
            <InboxOutlined style={{ fontSize: 20 }} />
            <span>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_INCOMING_BOOKINGS_NAV_BUTTON)}{' '}
              <Text type="secondary">
                ({pendingIncoming} {translate(t, TRANSLATIONS.HOST_PROFILE_BOOKINGS_PENDING_LABEL)})
              </Text>
            </span>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default HostProfileSection;
